// src/components/lesson/SubmitView.jsx
import { useState, useEffect, useRef } from 'react';
import { lmsAPI, uploadFiles } from '../../services/api';

export default function SubmitView({ lesson, enrollmentId, done, onComplete }) {
  const [existing,  setExisting]  = useState(null);
  const [text,      setText]      = useState('');
  const [link,      setLink]      = useState('');
  const [files,     setFiles]     = useState([]);      // File objects
  const [uploading, setUploading] = useState(false);
  const [submitting,setSubmitting]= useState(false);
  const [error,     setError]     = useState('');
  const fileRef = useRef();

  useEffect(() => {
    lmsAPI.mySubmission(lesson.id, enrollmentId)
      .then(r => { const s = r.data.data; if (s) { setExisting(s); } })
      .catch(() => {});
  }, [lesson.id, enrollmentId]);

  const handleSubmit = async () => {
    if (!text.trim() && !link.trim() && files.length === 0) {
      setError('Please add text, a link, or upload at least one file.'); return;
    }
    setError(''); setSubmitting(true);
    try {
      let fileUrls = [];
      if (files.length > 0) {
        setUploading(true);
        fileUrls = await uploadFiles(files);
        setUploading(false);
      }
      await lmsAPI.submit(lesson.id, {
        enrollment_id: enrollmentId,
        text_response: text || null,
        link_url:      link || null,
        file_urls:     fileUrls,
      });
      setExisting({ status:'submitted', file_urls:fileUrls, text_response:text, link_url:link });
      onComplete();
    } catch (e) {
      setError(e.response?.data?.error || 'Submission failed'); setUploading(false);
    } finally { setSubmitting(false); }
  };

  const removeFile = (i) => setFiles(f => f.filter((_,j)=>j!==i));

  const statusColors = {
    submitted:          { bg:'rgba(78,156,245,0.1)',  border:'rgba(78,156,245,0.25)',  color:'var(--blue)', label:'Submitted — awaiting review' },
    under_review:       { bg:'rgba(232,164,58,0.08)', border:'var(--gold-border)',     color:'var(--gold)', label:'Under review' },
    approved:           { bg:'rgba(62,207,142,0.1)',  border:'rgba(62,207,142,0.25)', color:'var(--teal)', label:'Approved ✓' },
    rejected:           { bg:'rgba(240,96,96,0.08)',  border:'rgba(240,96,96,0.25)',  color:'var(--red)',  label:'Needs revision' },
    resubmit_requested: { bg:'rgba(240,96,96,0.08)',  border:'rgba(240,96,96,0.25)',  color:'var(--red)',  label:'Resubmission requested' },
  };

  // ── Existing submission ───────────────────────────────
  if (existing) {
    const sc = statusColors[existing.status] || statusColors.submitted;
    const canResubmit = ['rejected','resubmit_requested'].includes(existing.status);

    return (
      <div style={{ maxWidth:640 }}>
        <div style={{ background:sc.bg, border:`1px solid ${sc.border}`, borderRadius:'var(--r-xl)', padding:24, marginBottom:24 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'1rem', fontWeight:600, color:sc.color }}>{sc.label}</div>
            {existing.score !== null && existing.score !== undefined && (
              <div style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem', fontWeight:700, color:sc.color }}>{existing.score}/100</div>
            )}
          </div>

          {existing.text_response && (
            <div style={{ fontSize:'0.9rem', color:'var(--t-secondary)', lineHeight:1.7, marginBottom:12 }}>{existing.text_response}</div>
          )}
          {existing.file_urls?.length > 0 && (
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
              {existing.file_urls.map((u,i) => (
                <a key={i} href={u} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize:'0.8rem', padding:'5px 12px', borderRadius:20, background:'rgba(255,255,255,0.05)', border:'1px solid var(--border)', color:'var(--blue)', textDecoration:'none' }}>
                  📎 File {i+1}
                </a>
              ))}
            </div>
          )}
          {existing.link_url && (
            <a href={existing.link_url} target="_blank" rel="noopener noreferrer"
              style={{ fontSize:'0.85rem', color:'var(--blue)' }}>🔗 {existing.link_url}</a>
          )}
          {existing.feedback && (
            <div style={{ marginTop:16, padding:'12px 16px', background:'rgba(255,255,255,0.04)', borderRadius:'var(--r-md)', border:'1px solid var(--border)' }}>
              <div style={{ fontSize:'0.68rem', color:'var(--t-muted)', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6 }}>Instructor Feedback</div>
              <div style={{ fontSize:'0.9rem', color:'var(--t-secondary)', lineHeight:1.7 }}>{existing.feedback}</div>
            </div>
          )}
        </div>

        {canResubmit && (
          <button className="btn btn-outline" onClick={() => setExisting(null)}>
            ↩ Resubmit
          </button>
        )}
      </div>
    );
  }

  // ── Submission form ───────────────────────────────────
  const typeConfig = {
    assignment:         { placeholder:'Explain your approach, what you built, and any challenges you faced…', fileLabel:'Upload your work (PDF, images, code files, etc.)', showLink:true },
    practical_evidence: { placeholder:'Describe what you did and what you learned…', fileLabel:'Upload photos or video of your practical work', showLink:false },
    portfolio:          { placeholder:'Describe your project — what it does, how you built it, what you learned…', fileLabel:'Upload project files or screenshots', showLink:true },
  };
  const cfg = typeConfig[lesson.lesson_type] || typeConfig.assignment;

  return (
    <div style={{ maxWidth:660 }}>
      <div style={{ display:'flex', flexDirection:'column', gap:18 }}>

        {/* Text response */}
        <div>
          <label style={{ display:'block', fontSize:'0.78rem', color:'var(--t-muted)', letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:8 }}>
            {lesson.lesson_type === 'practical_evidence' ? 'Description *' : 'Written Response'}
          </label>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={5}
            placeholder={cfg.placeholder}
            style={{ width:'100%', background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--t-primary)', padding:'13px 15px', fontSize:'0.9rem', resize:'vertical', lineHeight:1.7, outline:'none', transition:'border-color 0.15s' }}
            onFocus={e => e.target.style.borderColor = 'var(--gold-border)'}
            onBlur={e => e.target.style.borderColor = 'var(--border)'}/>
        </div>

        {/* File upload */}
        <div>
          <label style={{ display:'block', fontSize:'0.78rem', color:'var(--t-muted)', letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:8 }}>{cfg.fileLabel}</label>
          <div onClick={() => fileRef.current.click()}
            style={{ border:'1.5px dashed var(--border)', borderRadius:'var(--r-lg)', padding:'24px', textAlign:'center', cursor:'pointer', transition:'all 0.15s' }}
            onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--gold-border)';e.currentTarget.style.background='var(--gold-dim)';}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='transparent';}}>
            <div style={{ fontSize:'1.5rem', marginBottom:6, opacity:0.5 }}>
              {lesson.lesson_type==='practical_evidence'?'📸':'📎'}
            </div>
            <div style={{ fontSize:'0.85rem', color:'var(--t-secondary)' }}>
              Click to select files
            </div>
            <div style={{ fontSize:'0.75rem', color:'var(--t-muted)', marginTop:4 }}>
              {lesson.lesson_type==='practical_evidence'?'Photos, videos (JPG, PNG, MP4)':'PDF, ZIP, images, code files'}
            </div>
          </div>
          <input ref={fileRef} type="file" multiple style={{ display:'none' }}
            accept={lesson.lesson_type==='practical_evidence'?'image/*,video/*':'*'}
            onChange={e => setFiles(f => [...f, ...Array.from(e.target.files)])}/>

          {files.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:10 }}>
              {files.map((f,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'var(--bg-2)', borderRadius:'var(--r-md)', border:'1px solid var(--border)' }}>
                  <span style={{ flex:1, fontSize:'0.85rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
                  <span style={{ fontSize:'0.75rem', color:'var(--t-muted)' }}>{(f.size/1024/1024).toFixed(1)}MB</span>
                  <button onClick={() => removeFile(i)} style={{ background:'none', border:'none', color:'var(--t-muted)', cursor:'pointer', fontSize:14 }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Link */}
        {cfg.showLink && (
          <div>
            <label style={{ display:'block', fontSize:'0.78rem', color:'var(--t-muted)', letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:8 }}>Link (GitHub, Figma, Drive, etc.)</label>
            <input className="input" type="url" value={link} onChange={e => setLink(e.target.value)} placeholder="https://github.com/..."/>
          </div>
        )}

        {error && (
          <div style={{ padding:'10px 14px', background:'rgba(240,96,96,0.1)', border:'1px solid rgba(240,96,96,0.25)', borderRadius:'var(--r-md)', fontSize:'0.84rem', color:'var(--red)' }}>{error}</div>
        )}

        <div style={{ display:'flex', gap:12, alignItems:'center', paddingTop:4 }}>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}
            style={{ padding:'12px 28px', fontSize:'0.95rem', fontWeight:600 }}>
            {uploading ? 'Uploading files…' : submitting ? 'Submitting…' : 'Submit Work →'}
          </button>
          <span style={{ fontSize:'0.8rem', color:'var(--t-muted)' }}>Your instructor will review and grade your submission.</span>
        </div>
      </div>
    </div>
  );
}
